import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { parseISO } from 'date-fns';
import { API } from 'api';
import { WeatherStationLatestObservation } from '../../weatherStations.types';
import {
  ServerObservationValues,
  transformObservationValues,
} from '../../utils/transformObservationValues';

type UseWeatherStationLatestObservationProps = {
  id?: number;
  pollInterval?: number;
};

type UseWeatherStationLatestObservationResult = Omit<
  UseQueryResult<WeatherStationLatestObservation, Error>,
  'data'
> & {
  latestObservation?: WeatherStationLatestObservation;
};

type ServerObservationTimestamps = {
  [K in keyof ServerObservationValues]?: string;
};

type ServerWeatherStationLatestObservation = {
  values: ServerObservationValues;
  timestamps: ServerObservationTimestamps;
};

const transformTimestamps = (
  timestamps: ServerObservationTimestamps,
): WeatherStationLatestObservation['timestamps'] => {
  const { precipitation_1hr: precipitation1HrTimestamp, ...restTimestamps } =
    timestamps;

  return {
    ...Object.fromEntries(
      Object.entries(restTimestamps)
        .filter(([, v]) => v != null)
        .map(([k, v]) => [k, parseISO(v)]),
    ),
    ...(precipitation1HrTimestamp && {
      precipitation1Hr: parseISO(precipitation1HrTimestamp),
    }),
    ...(timestamps.windDirection && {
      windDirectionToward: parseISO(timestamps.windDirection),
    }),
  };
};

const fetchWeatherStationLatestObservation = async (
  stationId: number,
): Promise<WeatherStationLatestObservation> => {
  const response = await API.get<ServerWeatherStationLatestObservation>(
    `weather/latest_observation/${stationId}/`,
  );

  const { values, timestamps } = response.data;

  return {
    values: transformObservationValues(values),
    timestamps: transformTimestamps(timestamps),
  };
};

export const useWeatherStationLatestObservation = (
  props: UseWeatherStationLatestObservationProps,
): UseWeatherStationLatestObservationResult => {
  const { id, pollInterval } = props;

  const { data, ...results } = useQuery({
    queryKey: ['weatherStationLatestObservation', id],
    queryFn: () => fetchWeatherStationLatestObservation(id!),
    enabled: Boolean(id),
    refetchInterval: pollInterval,
  });

  return {
    ...results,
    latestObservation: data,
  };
};
